import React, { useCallback } from 'react';
import Keyboard from '@uber/base-icons/keyboard';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { NotificationCircle } from 'baseui/badge';
import type { ButtonOverrides } from 'baseui/button';
import { Button, KIND, SIZE } from 'baseui/button';
import { ALIGN, HeaderNavigation, StyledNavigationItem, StyledNavigationList } from 'baseui/header-navigation';
import { StatefulTooltip } from 'baseui/tooltip';
import { HeadingSmall, LabelLarge } from 'baseui/typography';
import { useAtomValue } from 'jotai';
import { selectedVideoIndexAtom } from '../../image/labelling-v2/atoms/multi-video-atoms';
import type { DrawerProps } from '../../modernization-common/side-panel/atoms/drawer.atom';
import { useRemainingItemsDrawer } from '../../modernization-common/side-panel-m4/hooks/use-remaining-items-drawer';
import { useEnableRemainingItemsM3Panel } from '../hooks/use-enable-remaining-items-m3-panel';
export type TaskHeaderProps = {
  pageName?: string;
  title: string;
  controls?: React.ReactNode;
  children: React.ReactNode;
  remainingItemsCount?: number;
  shouldShowRemainingItems?: boolean;
  reviewControls?: React.ReactNode;
  remainingItemsDrawerProps?: Omit<DrawerProps, 'onClose'>;
  onRemainingItemsClick?: () => void;
  showKeyBoardShortcut?: boolean;
  onKeyboardShortcutClick?: () => void;
  bypassRemainingItemsFeatureFlag?: boolean;
  pageControls?: React.ReactNode;
};
const REMAINING_ITEMS_BUTTON_OVERRIDES: ButtonOverrides = {
  BaseButton: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      padding: `${$theme.sizing.scale400} ${$theme.sizing.scale500}`,
      borderRadius: $theme.sizing.scale300,
      fontSize: $theme.sizing.scale500
    })
  },
  StartEnhancer: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      fontSize: $theme.sizing.scale500
    })
  }
};
const TASK_BAR_BUTTON_OVERRIDES: ButtonOverrides = {
  BaseButton: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      padding: $theme.sizing.scale200,
      borderRadius: $theme.sizing.scale300,
      height: $theme.sizing.scale850,
      width: $theme.sizing.scale850,
      boxSizing: 'border-box'
    })
  }
};
export default function TaskHeader({
  pageName,
  title,
  children,
  controls,
  reviewControls,
  remainingItemsCount = 0,
  shouldShowRemainingItems = false,
  remainingItemsDrawerProps,
  onRemainingItemsClick,
  showKeyBoardShortcut = false,
  onKeyboardShortcutClick,
  bypassRemainingItemsFeatureFlag = false,
  pageControls
}: TaskHeaderProps) {
  const [css, theme] = useStyletron();
  const featureFlagEnabled = useEnableRemainingItemsM3Panel(shouldShowRemainingItems);
  const isRemainingItemsSidePanelM3Enabled = bypassRemainingItemsFeatureFlag ? shouldShowRemainingItems : featureFlagEnabled;
  const {
    openRemainingItemsDrawer
  } = useRemainingItemsDrawer({
    remainingItemsDrawerProps
  });
  const handleRemainingItemsClick = useCallback(() => {
    if (onRemainingItemsClick) {
      onRemainingItemsClick();
    } else {
      openRemainingItemsDrawer();
    }
  }, [onRemainingItemsClick, openRemainingItemsDrawer]);
  const selectedIndex = useAtomValue(selectedVideoIndexAtom);
  const isMultiVideoIndex = selectedIndex !== 0;
  return <HeaderNavigation overrides={{
    Root: {
      style: ({
        $theme
      }) => ({
        background: $theme.colors.backgroundPrimary,
        borderBottom: `2px solid ${$theme.colors.borderOpaque}`,
        paddingRight: $theme.sizing.scale500,
        height: $theme.sizing.scale1400,
        boxSizing: 'border-box'
      })
    }
  }}>
      <StyledNavigationList $align={ALIGN.left}>
        {pageName && <>
            <StyledNavigationItem>
              <HeadingSmall>{pageName}</HeadingSmall>
            </StyledNavigationItem>
            <StyledNavigationItem className={css({
          paddingLeft: theme.sizing.scale300
        })}>
              <LabelLarge>·</LabelLarge>
            </StyledNavigationItem>
          </>}
        <StyledNavigationItem className={css({
        paddingLeft: theme.sizing.scale300
      })}>
          <LabelLarge>{title}</LabelLarge>
        </StyledNavigationItem>
      </StyledNavigationList>
      <StyledNavigationList $align={ALIGN.center}>{controls}</StyledNavigationList>
      <StyledNavigationList $align={ALIGN.right}>
        {reviewControls}
        {showKeyBoardShortcut && <StyledNavigationItem $style={{
        paddingLeft: theme.sizing.scale200
      }}>
            <StatefulTooltip content="Keyboard shortcuts" placement="bottom">
              <Button onClick={onKeyboardShortcutClick} overrides={TASK_BAR_BUTTON_OVERRIDES} kind={KIND.tertiary} data-testid="keyboard-shortcuts-button">
                <Keyboard size={14} />
              </Button>
            </StatefulTooltip>
          </StyledNavigationItem>}
        {isRemainingItemsSidePanelM3Enabled && <>
            <Button size={SIZE.small} kind={KIND.tertiary} overrides={REMAINING_ITEMS_BUTTON_OVERRIDES} startEnhancer={() => <NotificationCircle content={remainingItemsCount} />} data-testid="remaining-items-button" onClick={handleRemainingItemsClick} disabled={isMultiVideoIndex}>
              Remaining items
            </Button>
          </>}
        <StyledNavigationItem $style={{
        paddingLeft: theme.sizing.scale200
      }}>
          {children}
        </StyledNavigationItem>
        {pageControls}
      </StyledNavigationList>
    </HeaderNavigation>;
}